import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, Navigate, useSearchParams } from "react-router-dom";
import { Layout } from "../components/tag/Layout";
import { HeroBg, EyebrowPill } from "../components/tag/Primitives";
import { api } from "../lib/api";
import { HUBS, HUB_MAP, CATEGORIES } from "../data/hubs";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

const trackCta = (slug, cta) => { api.post("/blog/cta-click", { slug, cta }).catch(() => {}); };

export const BlogIndex = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const hub = params.get("hub") || "";
  const category = params.get("category") || "";
  const q = params.get("q") || "";
  const [search, setSearch] = useState(q);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/blog", { params: { hub: hub || undefined, category: category || undefined, q: q || undefined } });
      setPosts(res.data || []);
    } finally { setLoading(false); }
  }, [hub, category, q]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const submitSearch = (e) => { e.preventDefault(); setFilter("q", search); };

  return (
    <Layout title="Blog | The Authority Gap" path="/blog">
      <section style={{ background: "#090909", padding: "70px 32px 36px", position: "relative", overflow: "hidden", textAlign: "center" }}>
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 760, margin: "0 auto" }}>
          <div style={{ marginBottom: 20 }}><EyebrowPill>Blog</EyebrowPill></div>
          <h1 className="tag-h1" style={{ marginBottom: 12 }}>Authority systems, in writing.</h1>
          <p style={{ color: "#aaa", fontSize: 15 }}>Frameworks, case studies, and tactics from the labs.</p>
          <form onSubmit={submitSearch} style={{ display: "flex", gap: 8, marginTop: 22, maxWidth: 460, marginLeft: "auto", marginRight: "auto" }}>
            <input data-testid="blog-search" className="tag-input" placeholder="Search articles…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <button type="submit" className="btn-primary">Search</button>
          </form>
        </div>
      </section>

      <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "30px 20px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ color: "#888", fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Hubs</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button data-testid="hub-all" onClick={() => setFilter("hub", "")} className="tag-pill" style={{ background: !hub ? "#FF6575" : "#111", color: !hub ? "#fff" : "#aaa", border: !hub ? "1px solid #FF6575" : "1px solid #222", cursor: "pointer" }}>All</button>
            {HUBS.map((h) => (
              <button key={h.slug} data-testid={`hub-${h.slug}`} onClick={() => setFilter("hub", h.slug)} className="tag-pill" style={{ background: hub === h.slug ? h.accent : "#111", color: hub === h.slug ? "#fff" : "#aaa", border: hub === h.slug ? `1px solid ${h.accent}` : "1px solid #222", cursor: "pointer" }}>
                {h.name}
              </button>
            ))}
          </div>
          {category && (
            <div style={{ marginTop: 12, color: "#777", fontSize: 12 }}>
              Filtered by category: <strong style={{ color: "#fff" }}>{category}</strong>{" "}
              <button onClick={() => setFilter("category", "")} style={{ background: "transparent", border: "none", color: "#FF6575", cursor: "pointer" }}>Clear</button>
            </div>
          )}
        </div>
      </section>

      <section style={{ background: "#090909", padding: "20px 20px 64px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          {loading && <div className="tag-card" style={{ textAlign: "center", color: "#777" }}>Loading…</div>}
          {!loading && posts.length === 0 && <div className="tag-card" style={{ textAlign: "center", color: "#bbb" }}>No articles match those filters.</div>}
          {posts.map((p) => {
            const h = HUB_MAP[p.hub];
            return (
              <Link key={p.id || p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="tag-card" style={{ display: "block", marginBottom: 12, textDecoration: "none", position: "relative", overflow: "hidden" }}>
                {h && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: h.accent }} />}
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
                  {h && <span style={{ color: h.accent, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h.name}</span>}
                  <span style={{ color: "#888", fontSize: 9, fontWeight: 600, textTransform: "uppercase" }}>{p.category}</span>
                  <span style={{ color: "#777", fontSize: 12 }}>{fmtDate(p.created_at)}</span>
                </div>
                <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700, margin: "0 0 8px" }}>{p.title}</h3>
                <p style={{ color: "#bbb", fontSize: 13, lineHeight: 1.65, margin: "0 0 10px" }}>{p.excerpt}</p>
                <div style={{ color: "#FF6575", fontSize: 12, fontWeight: 600 }}>Read →</div>
              </Link>
            );
          })}
        </div>
      </section>
    </Layout>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setMissing(true)).finally(() => setLoading(false));
  }, [slug]);

  // Inject JSON-LD schema once post loads
  useEffect(() => {
    if (!post) return;
    const ld = {
      "@context": "https://schema.org", "@type": "Article",
      headline: post.title,
      description: post.meta_description || post.excerpt,
      datePublished: post.created_at,
      author: { "@type": "Organization", name: "The Authority Gap" },
      publisher: { "@type": "Organization", name: "The Authority Gap" },
    };
    let s = document.getElementById("ld-article");
    if (!s) { s = document.createElement("script"); s.type = "application/ld+json"; s.id = "ld-article"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
    return () => { const el = document.getElementById("ld-article"); if (el) el.remove(); };
  }, [post]);

  if (missing) return <Navigate to="/blog" replace />;
  const hub = post ? HUB_MAP[post.hub] : null;

  return (
    <Layout title={post ? (post.seo_title || `${post.title} | The Authority Gap`) : "Loading…"} path={`/blog/${slug}`}>
      <section style={{ background: "#090909", padding: "60px 20px 32px", position: "relative", overflow: "hidden" }}>
        <HeroBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 760, margin: "0 auto" }}>
          {loading && <div style={{ color: "#777" }}>Loading…</div>}
          {post && (
            <>
              <div style={{ marginBottom: 18, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                {hub && <Link to={`/blog?hub=${hub.slug}`} style={{ color: hub.accent, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>{hub.name}</Link>}
                <span style={{ color: "#888", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>{post.category}</span>
                <span style={{ color: "#777", fontSize: 12 }}>{fmtDate(post.created_at)}</span>
              </div>
              <h1 className="tag-h1" style={{ marginBottom: 14, fontSize: 36 }}>{post.title}</h1>
              <p style={{ color: "#aaa", fontSize: 15, lineHeight: 1.6 }}>{post.excerpt}</p>
            </>
          )}
        </div>
      </section>

      {post && (
        <section style={{ background: "#090909", borderTop: "1px solid #1a1a1a", padding: "40px 20px 60px" }}>
          <div style={{ maxWidth: 760, margin: "0 auto" }}>
            <article data-testid="blog-article" className="tag-card" style={{ padding: 36 }}>
              <div style={{ color: "#bbb", fontSize: 14, lineHeight: 1.85, whiteSpace: "pre-wrap" }}>{post.body}</div>

              {/* Internal CTAs */}
              <div style={{ marginTop: 28, paddingTop: 22, borderTop: "1px solid #1a1a1a", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }} className="blog-ctas">
                <Link to="/score" onClick={() => trackCta(post.slug, "score")} className="btn-primary" data-testid="cta-score" style={{ textAlign: "center" }}>Take the Authority Score</Link>
                <Link to="/resources/authority-gap-playbook" onClick={() => trackCta(post.slug, "playbook")} className="btn-ghost" data-testid="cta-playbook" style={{ textAlign: "center" }}>Get the Playbook</Link>
                <Link to="/atlas" onClick={() => trackCta(post.slug, "atlas")} className="btn-ghost" data-testid="cta-atlas" style={{ textAlign: "center" }}>Talk to Atlas</Link>
              </div>

              {/* FAQ */}
              {post.faq && post.faq.length > 0 && (
                <div style={{ marginTop: 32 }}>
                  <h3 style={{ color: "#fff", fontSize: 18, fontWeight: 700, margin: "0 0 14px" }}>Frequently asked</h3>
                  {post.faq.map((f) => (
                    <div key={f.q} style={{ borderBottom: "1px solid #1a1a1a", padding: "12px 0" }}>
                      <div style={{ color: "#fff", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{f.q}</div>
                      <div style={{ color: "#bbb", fontSize: 13, lineHeight: 1.6 }}>{f.a}</div>
                    </div>
                  ))}
                </div>
              )}
            </article>

            {/* Related */}
            {post.related && post.related.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h3 style={{ color: "#fff", fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>Related reading</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }} className="related-grid">
                  {post.related.map((r) => (
                    <Link key={r.slug} to={`/blog/${r.slug}`} className="tag-lab-card" style={{ textDecoration: "none", display: "block" }}>
                      <h4 style={{ color: "#fff", fontSize: 13, fontWeight: 700, margin: "0 0 8px" }}>{r.title}</h4>
                      <p style={{ color: "#bbb", fontSize: 11, lineHeight: 1.55, margin: 0 }}>{r.excerpt}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
          <style>{`
            @media (max-width: 720px){
              .blog-ctas{ grid-template-columns: 1fr !important; }
              .related-grid{ grid-template-columns: 1fr !important; }
            }
          `}</style>
        </section>
      )}
    </Layout>
  );
}
